import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Where you were, remembered two ways.
 *
 * `byHistoryEntry` is per browser history entry — going back puts you exactly
 * where you left that particular visit. `byPath` is per screen, and only for
 * screens that ask for it (`data-sticky-scroll`): the tabs keep their place
 * the way a native app's tabs do, however you arrive back at them.
 *
 * Module-level on purpose: both have to outlive the page components being
 * swapped out, but shouldn't survive a reload.
 */
const byHistoryEntry = new Map<string, number>();
const byPath = new Map<string, number>();

/**
 * How often this has run and how many times it has written a scroll position,
 * so the debug readout can say whether a page that won't scroll up is being
 * put back by this or by something else. Diagnostic only.
 */
export const restoreStats = { runs: 0, writes: 0, target: 0 };

// The browser restores document scroll itself when you go back, and does it
// after the app has re-rendered — undoing the position this component just
// set. Taking manual control stops the two fighting each other.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/**
 * The element that actually scrolls, looked up fresh every time rather than
 * held onto: a page that swaps its skeleton for real content mounts a whole
 * new container, and a reference captured before that swap would be pointing
 * at a detached node by the time the content it was waiting for arrives.
 */
const getScroller = () =>
  document.querySelector<HTMLElement>("[data-scroll-container]") ??
  ((document.scrollingElement ?? document.documentElement) as HTMLElement | null);

/** Screens that keep their place across visits opt in; the rest open at the top. */
const isSticky = () => !!document.querySelector("[data-scroll-container][data-sticky-scroll]");

/**
 * Sends you to the top of a page you've just opened, and back to where you
 * were on one you're returning to. Replaces an unconditional scroll-to-top,
 * which threw away your place in the calendar every time you opened an event
 * or switched tabs.
 */
const ScrollRestoration = () => {
  const { key, pathname } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    restoreStats.runs += 1;
    const sticky = isSticky();
    const remembered = sticky ? byPath.get(pathname) : undefined;
    const target =
      navigationType === "POP"
        ? (byHistoryEntry.get(key) ?? remembered ?? 0)
        : (remembered ?? 0);

    // The page may still be showing its skeleton and be too short to hold the
    // old offset, in which case the browser clamps what we set and we land
    // part-way up. So: put the reader there once, and if it didn't take,
    // *watch* for the content to arrive and place them one more time.
    //
    // This used to re-assert the position on every frame until the content
    // showed up, which meant anyone scrolling during that second was dragged
    // back on each one — the page felt stuck at a spot a little way down. It
    // now writes scrollTop at most twice, and never after the reader moves.
    let settled = false;
    let frames = 0;
    let raf = 0;

    restoreStats.target = target;

    const place = () => {
      const el = getScroller();
      if (!el) return true;
      restoreStats.writes += 1;
      el.scrollTop = target;
      return el.scrollHeight - el.clientHeight >= target;
    };

    // Always write, even for the top: React can reuse one scroll container
    // across two routes, and it would arrive still carrying the last page's
    // offset.
    if (place()) {
      settled = true;
    } else {
      const waitForContent = () => {
        if (settled) return;
        const el = getScroller();
        const reachable = el ? el.scrollHeight - el.clientHeight : 0;
        if (reachable >= target) place();
        else if (frames++ < 90) {
          raf = requestAnimationFrame(waitForContent);
          return;
        }
        settled = true;
      };
      raf = requestAnimationFrame(waitForContent);
    }

    // The reader outranks us: the first sign of a scroll and we stop trying to
    // put them anywhere, and start remembering where they went instead.
    const yieldToReader = () => {
      cancelAnimationFrame(raf);
      settled = true;
    };
    const READER_EVENTS = ["wheel", "touchstart", "keydown"] as const;
    READER_EVENTS.forEach((type) =>
      document.addEventListener(type, yieldToReader, { passive: true, capture: true }),
    );

    const onScroll = () => {
      // Never record a position while still waiting to restore: an assignment
      // the browser clamped would otherwise overwrite the offset we're trying
      // to return to, and the page would settle at the top after all.
      if (!settled) return;
      const el = getScroller();
      if (!el) return;
      byHistoryEntry.set(key, el.scrollTop);
      if (isSticky()) byPath.set(pathname, el.scrollTop);
    };
    // Capture phase on the document, because scroll events don't bubble and
    // the element doing the scrolling can be replaced under us mid-visit.
    document.addEventListener("scroll", onScroll, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("scroll", onScroll, true);
      READER_EVENTS.forEach((type) =>
        document.removeEventListener(type, yieldToReader, { capture: true }),
      );
    };
  }, [key, pathname, navigationType]);

  return null;
};

export default ScrollRestoration;
